import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "filters-bar" }
const _hoisted_3 = ["value"]
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "toast-content" }
const _hoisted_6 = { class: "toast-icon" }
const _hoisted_7 = { class: "toast-message" }
const _hoisted_8 = {
  key: 1,
  class: "total-banner"
}
const _hoisted_9 = {
  key: 2,
  class: "schedule-grid"
}
const _hoisted_10 = { class: "time-col" }
const _hoisted_11 = { class: "job-col" }
const _hoisted_12 = { class: "seq" }
const _hoisted_13 = { class: "job-info" }
const _hoisted_14 = { class: "name" }
const _hoisted_15 = { class: "site" }
const _hoisted_16 = { class: "meta" }
const _hoisted_17 = { class: "actions" }
const _hoisted_18 = ["disabled", "onClick", "title"]
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["onClick"]
const _hoisted_21 = {
  key: 3,
  class: "no-jobs-message"
}
const _hoisted_22 = { class: "text-danger" }
const _hoisted_23 = { class: "sia-modal" }
const _hoisted_24 = { class: "sia-modal-header" }
const _hoisted_25 = { class: "sia-modal-body" }
const _hoisted_26 = { class: "sia-row text-primary" }
const _hoisted_27 = { class: "sia-row" }
const _hoisted_28 = { class: "sia-row" }
const _hoisted_29 = { class: "sia-row text-primary" }
const _hoisted_30 = { class: "sia-row text-primary" }
const _hoisted_31 = { class: "sia-row text-primary" }
const _hoisted_32 = { class: "sia-row text-primary" }
const _hoisted_33 = { class: "sia-row" }
const _hoisted_34 = { class: "sia-row" }

import { ref, computed, onMounted, watch } from 'vue'
import JobCallsModal from "@/components/JobCallsModal.vue";
import lookupDataService from "@/services/LookupDataService";

// ============ TYPES ============
interface Company {
  companyId: number
  companyName: string
}

interface Job {
  jobId: number
  jobDate: string
  startTime: string | { Hours: number; Minutes: number }
  endTime: string | { Hours: number; Minutes: number }
  securityOfficerInfo: string
  securityOfficerId: number
  siteInfo: string
  clientInfo: string
  statusId: number
  siaBadgeNumber: string
  pinNumber?: string
  isSiaBadgeVerified?: boolean | null,
  mobileNumber?: string | null,
}

interface SiaResult {
  siaNumber: string
  employeePin: string
  employeeName: string
  licenseName: string
  role: string
  licenseType: string
  expiryDate: string
  status: string
  statusDate: string
  remarks: string
}

interface VerifyBadgeResult {
  status: string
  data?: {
    licenceNumber: string
    fullName: string
    role: string
    licenceSector: string
    expiryDate: string
    status: string
    statusDate: string
  }
  message?: string
}

// ============ PROPS ============

export default /*@__PURE__*/_defineComponent({
  __name: 'JobCallsView',
  props: {
    baseUrl: {}
  },
  setup(__props: any) {

const props = __props

// ============ DATA ============
const selectedDate = ref<Date>(new Date())
const jobs = ref<Job[]>([])
const companies = ref<Company[]>([])
const selectedCompanyId = ref<string>("")
const showToast = ref<boolean>(false)
const toastMessage = ref<string>("")
const toastType = ref<string>("info")
const toastTimeout = ref<number | null>(null)
const hours: number[] = [...Array(24).keys()]
const showSiaModal = ref<boolean>(false)
const todayString: string = new Date().toDateString()
const siaResult = ref<SiaResult>({
  siaNumber: '',
  employeePin: '',
  employeeName: '',
  licenseName: '',
  role: '',
  licenseType: '',
  expiryDate: '',
  status: '',
  statusDate: '',
  remarks: ''
});

const jobCallsModal = ref(null)

// ============ COMPUTED ============
const toastIcon = computed<string>(() => {
  const icons: Record<string, string> = { 
    success: "✅", 
    error: "❌", 
    warning: "⚠️", 
    info: "ℹ️" 
  }
  return icons[toastType.value] || "ℹ️"
})

const grandTotalHours = computed<string>(() => {
  let total = 0
  for (const j of jobs.value) {
    const start = toMinutes(j.startTime)
    const end = toMinutes(j.endTime)
    total += end - start
  }
  const h = Math.floor(total / 60)
  const m = total % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
})

const filteredHours = computed<number[]>(() => {
  const hoursWithJobs = new Set<number>()
  for (const j of jobs.value) {
    const h = Math.floor(toMinutes(j.startTime) / 60)
    hoursWithJobs.add(h)
  }
  const minHour = Math.min(...hoursWithJobs)
  const maxHour = Math.max(...hoursWithJobs)
  const result: number[] = []
  for (let h = minHour; h <= maxHour; h++) {
    if (hoursWithJobs.has(h)) result.push(h)
  }
  return result.length ? result : hours
})

// ============ METHODS ============
// UPDATED: Use cached data from localStorage instead of API call
function loadCompanies(): void {
  try {
    // Read from localStorage (cached by login)
    const cachedData = localStorage.getItem("lookupData");
    
    if (!cachedData) {
      console.warn("No cached data found. Please login again.");
      return;
    }
    
    const data = JSON.parse(cachedData);
    
    let companiesData = data.companies || [];
    if (companiesData.data && Array.isArray(companiesData.data)) {
      companiesData = companiesData.data;
    }
    if (companiesData.values && Array.isArray(companiesData.values)) {
      companiesData = companiesData.values;
    }
    
    companies.value = companiesData.map((c: any) => ({
      companyId: c.companyId || c.id || c.Id || 0,
      companyName: c.companyName || c.name || c.Name || 'Unknown'
    }));

    // Store callTypes if available
    if (data.callTypes) {
      localStorage.setItem("callTypes", JSON.stringify(data.callTypes));
    }
    
  } catch (e) {
    console.error("Failed to load companies from cache:", e);
  }
}

function closeToast(e: Event): void {
  e.stopPropagation()
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value)
    toastTimeout.value = null
  }
  showToast.value = false
  toastMessage.value = ""
}

function showNotification(message: string, type: string = "info"): void {
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value)
    toastTimeout.value = null
  }
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  toastTimeout.value = setTimeout(() => {
    showToast.value = false
  }, 5000) as unknown as number
}

async function loadJobs(): Promise<void> {
  const res = await fetch(props.baseUrl + "schedule/ScheduleDictionaryForDateRangePageWise", {
    method: "POST",
    headers: { 
      Authorization: `Bearer ${localStorage.token}`, 
      "Content-Type": "application/json" 
    },
    body: JSON.stringify({
      StartDate: selectedDate.value,
      EndDate: selectedDate.value,
      PageNumber: 0,
      RecordsPerPage: 1024 * 1024,
      CompanyId: parseInt(selectedCompanyId.value) || 0
    })
  })
  const data = await res.json()
  const key = getDateKey(selectedDate.value)
  jobs.value = data.scheduleSinglePageResult?.[key]?.scheduleViewModels || []
}

function getJobsForHour(hour: number): Job[] {
  return jobs.value.filter(j => {
    const m = toMinutes(j.startTime)
    return m >= hour * 60 && m < (hour + 1) * 60
  })
}

function toMinutes(t: string | { Hours: number; Minutes: number }): number {
  if (!t) return 0
  if (typeof t === 'string') {
    const parts = t.split(':')
    return parseInt(parts[0]) * 60 + parseInt(parts[1])
  }
  return t.Hours * 60 + (t.Minutes || 0)
}

function formatTime(t: string | { Hours: number; Minutes: number }): string {
  if (!t) return '--:--'
  if (typeof t === 'string') return t.substring(0, 5)
  return `${String(t.Hours).padStart(2, '0')}:${String(t.Minutes || 0).padStart(2, '0')}`
}

function duration(job: Job): string {
  const start = toMinutes(job.startTime)
  const end = toMinutes(job.endTime)
  const mins = end - start
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function getDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T00:00:00`
}

function dateToYYYYMMDD(d: Date): string {
  return d.toISOString().split('T')[0]
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
}

function pad(n: number): string { 
  return String(n).padStart(2, '0') 
}

function statusClass(id: number): Record<string, boolean> {
  return {
    'status-completed': id == 2,
    'status-in-progress': id == 4,
    'status-pending': id == 3,
    'status-cancelled': id == 8
  }
}

function getSeq(id: number): number {
  return jobs.value.findIndex(j => j.jobId === id) + 1
}

async function verifyBadge(badge: string, jobId: number, securityOfficerId: number): Promise<VerifyBadgeResult> {
  const cleanBadge = badge.replace(/[\s-]/g, '')
  
  if (!cleanBadge || cleanBadge.length !== 16) {
    showNotification('Invalid badge number format', 'error')
    return { status: 'InvalidFormat' }
  }
  
  try {
    const res = await fetch(props.baseUrl + "schedule/VerifyBadge", {
      method: "POST",
      headers: { 
        Authorization: `Bearer ${localStorage.token}`, 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({
        BadgeNumber: cleanBadge,
        JobId: jobId,
        SecurityOfficerId: securityOfficerId
      })
    })

    const result = await res.json()
    return result
    
  } catch(e) {
    console.error(e)
    return { status: 'Error' }
  }
}

function handleSIABadgeVerification(job: Job): void {
  const badge = job.siaBadgeNumber
  const jobId = job.jobId
  const securityOfficerId = job.securityOfficerId
  const cleanBadge = badge ? badge.replace(/[\s-]/g, '') : ''
  
  verifyBadge(cleanBadge, jobId, securityOfficerId).then(result => {
    const index = jobs.value.findIndex(j => j.jobId === job.jobId)
    if (index !== -1) {
      jobs.value[index].isSiaBadgeVerified = (result.status === 'OK')
      jobs.value = [...jobs.value]
    }
    
    if (result.data) {
      siaResult.value = {
        siaNumber: result.data.licenceNumber || cleanBadge,
        employeePin: job.pinNumber || '',
        employeeName: job.securityOfficerInfo || 'Not Found',
        licenseName: result.data.fullName || '',
        role: result.data.role || '',
        licenseType: result.data.licenceSector || '',
        expiryDate: result.data.expiryDate || '',
        status: result.data.status || 'Unknown',
        statusDate: new Date().toLocaleDateString(),
        remarks: result.data.status === 'Active' ? 'OK' : 'CLOSE'
      }
    } else {
      siaResult.value = {
        siaNumber: cleanBadge,
        employeePin: job.pinNumber || '',
        employeeName: job.securityOfficerInfo || 'Not Found',
        licenseName: 'Not Found',
        role: 'N/A',
        licenseType: 'N/A',
        expiryDate: 'N/A',
        status: result.status || 'NotFound',
        statusDate: '',
        remarks: 'No licence found for this badge number'
      }
    }
    showSiaModal.value = true
    
    if (result.status === 'OK') {
      showNotification('SIA Badge ✅ Verified', 'success')
    } else if (result.status === 'InActive') {
      showNotification('SIA Badge ❌ Inactive or Expired', 'error')
    } else if (result.status === 'NotFound') {
      showNotification('SIA Badge ❌ Not Found', 'error')
    } else {
      showNotification(`SIA Badge ❌ ${result.status}`, 'error')
    }
  })
}

async function handleJobCalls(job: Job): Promise<any> {
  try {
    const res = await fetch(props.baseUrl + "JobCalls/CallsForJob", {
      method: "POST",
      headers: { 
        Authorization: `Bearer ${localStorage.token}`, 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({
        id: job.jobId,
      })
    })

    const result = await res.json()

    if (jobCallsModal.value) {
      jobCallsModal.value.openForEdit(result.jobCalls)
    }
    
  } catch(e) {
    console.error(e)
    return { status: 'Error' }
  }
}

function handleChaseupCalls(job: Job): void {
  showNotification(`Chaseup Calls for ${job.securityOfficerInfo || 'Job #' + job.jobId}`, "info")
}

// ============ LIFECYCLE ============
onMounted(() => {
  loadCompanies()
  loadJobs()
})

// ============ WATCHERS ============
watch(selectedCompanyId, () => {
  loadJobs()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("input", {
          type: "date",
          class: "filter-date-width",
          value: dateToYYYYMMDD(selectedDate.value),
          onInput: _cache[0] || (_cache[0] = ($event: any) => {selectedDate.value = $event.target.valueAsDate; loadJobs()})
        }, null, 40, _hoisted_3),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedCompanyId).value = $event)),
          class: "filter-clients-width"
        }, [
          _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "All Clients", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(companies.value, (c) => {
            return (_openBlock(), _createElementBlock("option", {
              key: c.companyId,
              value: c.companyId
            }, _toDisplayString(c.companyName), 9, _hoisted_4))
          }), 128))
        ], 512), [
          [_vModelSelect, selectedCompanyId.value]
        ])
      ]),
      (showToast.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["toast-container", toastType.value])
          }, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(toastIcon.value), 1),
              _createElementVNode("span", _hoisted_7, _toDisplayString(toastMessage.value), 1),
              _createElementVNode("span", {
                class: "toast-close",
                onClick: closeToast,
                style: {"pointer-events":"auto","position":"relative","z-index":"9999"}
              }, "×")
            ])
          ], 2))
        : _createCommentVNode("", true),
      (jobs.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, " Total: " + _toDisplayString(grandTotalHours.value) + " (" + _toDisplayString(jobs.value.length) + " jobs) - " + _toDisplayString(formatDayLabel(selectedDate.value)), 1))
        : _createCommentVNode("", true),
      (jobs.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "grid-header" }, [
              _createElementVNode("div", { class: "time-col" }, "Time"),
              _createElementVNode("div", { class: "job-col" }, "Jobs")
            ], -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredHours.value, (hour) => {
              return (_openBlock(), _createElementBlock("div", {
                key: hour,
                class: "grid-row"
              }, [
                _createElementVNode("div", _hoisted_10, _toDisplayString(pad(hour)) + ":00", 1),
                _createElementVNode("div", _hoisted_11, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getJobsForHour(hour), (job) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: job.jobId,
                      class: _normalizeClass(["job-block", statusClass(job.statusId)])
                    }, [
                      _createElementVNode("span", _hoisted_12, _toDisplayString(getSeq(job.jobId)), 1),
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("div", _hoisted_14, _toDisplayString(job.securityOfficerInfo) + "-" + _toDisplayString(job.mobileNumber), 1),
                        _createElementVNode("div", _hoisted_15, _toDisplayString(job.siteInfo), 1),
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("span", null, _toDisplayString(formatTime(job.startTime)) + "-" + _toDisplayString(formatTime(job.endTime)), 1),
                          _createElementVNode("span", null, _toDisplayString(duration(job)), 1),
                          _createElementVNode("span", null, _toDisplayString(job.clientInfo), 1)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("button", {
                          class: _normalizeClass(["btn-action btn-sia", {
                  'sia-verified': job.isSiaBadgeVerified === true,
                  'sia-unverified': job.isSiaBadgeVerified === false,
                  'sia-unknown': job.isSiaBadgeVerified === null || job.isSiaBadgeVerified === undefined
                }]),
                          disabled: new Date(job.jobDate) < new Date((_unref(todayString))) || new Date(job.jobDate) > new Date((_unref(todayString))).getTime() + 24 * 60 * 60 * 1000 ,
                          onClick: _withModifiers(($event: any) => (new Date(job.jobDate) >= new Date((_unref(todayString))) && handleSIABadgeVerification(job)), ["stop"]),
                          title: new Date(job.jobDate).getTime() === new Date((_unref(todayString))).getTime() || new Date(job.jobDate).getTime() === new Date((_unref(todayString))).getTime()+24 * 60 * 60 * 1000 ? ' SIA Badge Verification' : 'SIA verification only available for today and tomorrow\'s shifts'
                        }, [...(_cache[5] || (_cache[5] = [
                          _createElementVNode("i", { class: "bi bi-shield-check" }, null, -1)
                        ]))], 10, _hoisted_18),
                        _createElementVNode("button", {
                          class: "btn-action btn-calls",
                          onClick: _withModifiers(($event: any) => (handleJobCalls(job)), ["stop"]),
                          title: "Job Calls"
                        }, [...(_cache[6] || (_cache[6] = [
                          _createElementVNode("i", { class: "bi bi-telephone" }, null, -1)
                        ]))], 8, _hoisted_19),
                        _createElementVNode("button", {
                          class: "btn-action btn-chase",
                          onClick: _withModifiers(($event: any) => (handleChaseupCalls(job)), ["stop"]),
                          title: "Chaseup Calls"
                        }, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("i", { class: "bi bi-clock-history" }, null, -1)
                        ]))], 8, _hoisted_20)
                      ])
                    ], 2))
                  }), 128))
                ])
              ]))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_21, [
            _createElementVNode("p", _hoisted_22, "No jobs found for " + _toDisplayString(formatDayLabel(selectedDate.value)), 1)
          ])),
      (showSiaModal.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 4,
            class: "sia-modal-overlay",
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (showSiaModal.value = false), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_23, [
              _createElementVNode("div", _hoisted_24, [
                _cache[9] || (_cache[9] = _createElementVNode("h4", { class: "heading-center" }, "SIA License Results", -1)),
                _createElementVNode("span", {
                  class: "sia-modal-close",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (showSiaModal.value = false))
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_25, [
                _createElementVNode("div", _hoisted_26, [
                  _cache[10] || (_cache[10] = _createElementVNode("label", null, "SIA Number", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.siaNumber), 1)
                ]),
                _createElementVNode("div", _hoisted_27, [
                  _cache[11] || (_cache[11] = _createElementVNode("label", null, "Name (In Our System)", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass({'name-mismatch': siaResult.value.employeeName !== siaResult.value.licenseName})
                  }, _toDisplayString(siaResult.value.employeeName), 3)
                ]),
                _createElementVNode("div", _hoisted_28, [
                  _cache[12] || (_cache[12] = _createElementVNode("label", null, "Name (On License)", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass({'name-mismatch': siaResult.value.employeeName !== siaResult.value.licenseName})
                  }, _toDisplayString(siaResult.value.licenseName), 3)
                ]),
                _createElementVNode("div", _hoisted_29, [
                  _cache[13] || (_cache[13] = _createElementVNode("label", null, "Role", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.role), 1)
                ]),
                _createElementVNode("div", _hoisted_30, [
                  _cache[14] || (_cache[14] = _createElementVNode("label", null, "License Type", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.licenseType), 1)
                ]),
                _createElementVNode("div", _hoisted_31, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", null, "Expiry Date", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.expiryDate), 1)
                ]),
                _createElementVNode("div", _hoisted_32, [
                  _cache[16] || (_cache[16] = _createElementVNode("label", null, "Status", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(siaResult.value.status === 'Active' ? 'sia-status-active' : 'sia-status-inactive')
                  }, _toDisplayString(siaResult.value.status), 3)
                ]),
                _createElementVNode("div", _hoisted_33, [
                  _cache[17] || (_cache[17] = _createElementVNode("label", null, "Status Date", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.statusDate), 1)
                ]),
                _createElementVNode("div", _hoisted_34, [
                  _cache[18] || (_cache[18] = _createElementVNode("label", null, "Remarks", -1)),
                  _createElementVNode("span", null, _toDisplayString(siaResult.value.remarks), 1)
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(JobCallsModal, {
      ref_key: "jobCallsModal",
      ref: jobCallsModal,
      baseUrl: __props.baseUrl
    }, null, 8, ["baseUrl"])
  ], 64))
}
}

})